/**
 * Utility functions for generating policy card information for different insurance types
 * Supports: Motor, Travel, Home, Personal Cyber, Smart Device, Personal Accident, Domestic Helper
 */

/**
 * Helper function to format dates as DD/MM/YYYY
 */
export const formatDateAsNumbers = (dateStr) => {
  if (!dateStr) return "N/A";

  try {
    let date;

    // Handle different date formats
    if (typeof dateStr === "string" && dateStr.includes("/")) {
      // Already in DD/MM/YYYY format
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${
          parts[2]
        }`;
      }
    }

    // Check if it's a unix timestamp (number or string number)
    const timestamp = typeof dateStr === "string" ? parseInt(dateStr) : dateStr;
    if (!isNaN(timestamp) && timestamp > 1000000000) {
      // Unix timestamp in seconds, convert to milliseconds
      date = new Date(timestamp * 1000);
    } else {
      // Try parsing as regular date
      date = new Date(dateStr);
    }

    if (isNaN(date.getTime())) {
      return dateStr; // Return original if can't parse
    }

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr || "N/A";
  }
};

/**
 * Helper function to calculate travel duration in days
 */
export const calculateTravelDays = (fromDateStr, toDateStr) => {
  if (!fromDateStr || !toDateStr) return "-- Days";

  try {
    const fromParts = fromDateStr.split("/");
    const toParts = toDateStr.split("/");
    const fromDate = new Date(fromParts[2], fromParts[1] - 1, fromParts[0]);
    const toDate = new Date(toParts[2], toParts[1] - 1, toParts[0]);
    const daysDiff = Math.ceil((toDate - fromDate) / (1000 * 3600 * 24));
    // Travel duration should be return date - departure date + 1
    return daysDiff > 0 ? `${daysDiff + 1} Days` : "-- Days";
  } catch {
    return "-- Days";
  }
};

/**
 * Helper function to map plan codes to readable names for travel
 * Returns object with English and Arabic names
 */
export const mapTravelPlanName = (planName, planCode, returnObject = false) => {
  let englishName = "Silver";
  let arabicName = "الفضة";

  if (planCode === "SILVER") {
    englishName = "Silver";
    arabicName = "الفضة";
  } else if (planCode === "GO") {
    englishName = "Gold";
    arabicName = "الذهب";
  } else if (planCode === "PL") {
    englishName = "Platinum";
    arabicName = "البلاتين";
  } else if (planCode === "WLEXB") {
    englishName = "Worldwide excluding USA & Canada";
    arabicName = "جميع أنحاء العالم باستثناء الولايات المتحدة وكندا";
  } else if (planCode === "WLB") {
    englishName = "Worldwide";
    arabicName = "جميع أنحاء العالم";
  } else if (planCode === "SCHENGENB") {
    englishName = "Schengen";
    arabicName = "شنغن";
  } else if (planName) {
    englishName = planName;
    // Try to detect from name
    const lowerName = planName.toLowerCase();
    if (lowerName.includes("gold")) {
      arabicName = "الذهب";
    } else if (lowerName.includes("platinum") || lowerName.includes("platinium")) {
      arabicName = "البلاتين";
    } else if (lowerName.includes("silver")) {
      arabicName = "الفضة";
    } else if (lowerName.includes("wlexb") || lowerName.includes("worldwide_ex") || lowerName.includes("worldwide excl")) {
      englishName = "Worldwide excluding USA & Canada";
      arabicName = "جميع أنحاء العالم باستثناء الولايات المتحدة وكندا";
    } else if (lowerName.includes("schengen")) {
      englishName = "Schengen";
      arabicName = "شنغن";
    } else if (lowerName.includes("wlb") || lowerName.includes("worldwide")) {
      englishName = "Worldwide";
      arabicName = "جميع أنحاء العالم";
    } else {
      arabicName = planName; // fallback
    }
  }

  if (returnObject) {
    return { english: englishName, arabic: arabicName };
  }
  return englishName; // backward compatibility
};

/**
 * Generate policy-specific display text based on product code
 * Returns an object with title and subtitle for the policy info section
 */
export const getPolicyDisplayInfo = (policyDetails) => {
  if (!policyDetails) return null;

  const productCode = policyDetails.ProductCode;

  switch (productCode) {
    case "0204": // Travel Insurance
      const planNames = mapTravelPlanName(
        policyDetails.PlanName,
        policyDetails.PlanCode,
        true
      );
      return {
        title: "Travel Insurance Policy",
        title_ar: "وثيقة تأمين السفر",
        subtitle: `${planNames.english} Plan`,
        subtitle_ar: `خطة ${planNames.arabic}`,
        showInCard: true,
      };

    case "0710": // Home Insurance
      return {
        title: "Home Insurance Policy",
        title_ar: "وثيقة التأمين المنزلي",
        subtitle: policyDetails.PropertyType || "Residential Property",
        subtitle_ar: policyDetails.PropertyType || "عقار سكني",
        showInCard: true,
      };

    case "0206": // Personal Cyber Insurance
      return {
        title: "Personal Cyber Insurance Policy",
        title_ar: "سياسة التأمين السيبراني الشخصي",
        subtitle: null,
        subtitle_ar: null,
        showInCard: true,
      };

    case "0213": // Smart Device Insurance
    case "0205": // Smart Device Insurance (legacy)
      return {
        title: "Smart Device Insurance",
        title_ar: "سياسة التأمين على الأجهزة الذكية",
        subtitle: null,
        subtitle_ar: null,
        showInCard: true,
      };

    case "0202": // Personal Accident Insurance
    case "0208": // Personal Accident Insurance
      return {
        title: "Personal Accident Insurance",
        title_ar: "سياسة التأمين على الحوادث الشخصية",
        subtitle: null,
        subtitle_ar: null,
        showInCard: true,
      };

    case "0222": // Domestic Helper Insurance
      return {
        title: "Domestic Helper Insurance Policy",
        title_ar: "سياسة تأمين المساعد المنزلي",
        subtitle: null,
        subtitle_ar: null,
        showInCard: true,
      };

    default:
      // Fallback: Check by name for personal cyber or smart device (in case different product code)
      const productName = policyDetails.ProductName?.toLowerCase() || "";
      const planName = policyDetails.PlanName?.toLowerCase() || "";
      if (
        productName.includes("personal cyber") ||
        productName.includes("cyber") ||
        planName.includes("personal cyber") ||
        planName.includes("cyber")
      ) {
        return {
          title: "Personal Cyber Insurance Policy",
          title_ar: "سياسة التأمين السيبراني الشخصي",
          subtitle: null,
          subtitle_ar: null,
          showInCard: true,
        };
      }
      if (
        productName.includes("smart device") ||
        productName.includes("device") ||
        planName.includes("smart device") ||
        planName.includes("device")
      ) {
        return {
          title: "Smart Device Insurance",
          title_ar: "سياسة التأمين على الأجهزة الذكية",
          subtitle: null,
          subtitle_ar: null,
          showInCard: true,
        };
      }

      // For any other product type, don't show the info section
      return {
        title: null,
        subtitle: null,
        showInCard: false,
      };
  }
};

/**
 * Generate complete policy card info object based on product code
 * Used in cancel policy flows and other endorsements
 * @param {Object} policyDetails - Policy details from API
 * @param {string} branch - Branch/channel identifier
 * @param {Object} profileData - Logged in user profile data (optional)
 */
export const generatePolicyCardInfo = (policyDetails, branch = "online.solidarity", profileData = null) => {
  if (!policyDetails) return null;

  const productCode = policyDetails.ProductCode;
  const baseInfo = {
    PolicyNumber: policyDetails.PolicyNumber || "--",
    CPR: "",
    SumInsured: policyDetails.SumInsured || 0.0,
    PlanName: policyDetails.PlanName || "--",
    ProductCode: productCode,
    StartDate:
      policyDetails.FromDateStr?.split(" ")[0]?.split("/")?.reverse()?.join("-") || "--",
    EndDate:
      policyDetails.ToDateStr?.split(" ")[0]?.split("/")?.reverse()?.join("-") || "--",
    FromDateStr: policyDetails.FromDateStr?.split(" ")[0] || "--",
    ToDateStr: policyDetails.ToDateStr?.split(" ")[0] || "--",
    Branch: branch,
    MainChannel: "Direct Bahrain",
    Renewed: policyDetails.IsRenewed,
    PolicyStatus: policyDetails.PolicyStatus || "Active",
    IsRenewable: policyDetails.IsRenewable || false,
  };

  switch (productCode) {
    case "0204": // Travel Insurance
      return {
        ...baseInfo,
        ModelYear: "",
        Make: "Travel Policy",
        Model: calculateTravelDays(policyDetails.FromDateStr, policyDetails.ToDateStr),
        RegistrationNumber: "",
        ProductName: "Travel",
      };

    case "0710": // Home Insurance
      return {
        ...baseInfo,
        ModelYear: "",
        Make: "Home Policy",
        Model: `Block No ${policyDetails.Block || "N/A"}, ${
          policyDetails.City || "N/A"
        }`,
        RegistrationNumber: policyDetails.PolicyNumber || "--",
        ProductName: "Home Policy",
      };

    case "0206": // Personal Cyber Insurance
      // Show CPR only (full name commented out for now)
      // Prioritize profileData (logged in user) like NonMotorPolicyCard does
      const cyberCpr = profileData?.CPR || policyDetails.CPR || policyDetails.InsuredCPR || "";
      // const cyberFirstName = profileData?.FirstName || policyDetails.Party?.FirstName || policyDetails.FirstName || "";
      // const cyberLastName = profileData?.LastName || policyDetails.Party?.LastName || policyDetails.LastName || "";
      // const cyberFullInfo = `${cyberCpr} ${cyberFirstName} ${cyberLastName}`.trim();
      const cyberFullInfo = cyberCpr; // Only CPR for now

      return {
        ...baseInfo,
        CPR: cyberCpr,
        ModelYear: "",
        Make: "Personal Cyber Insurance",
        Model: cyberFullInfo || "Personal Cyber Policy",
        RegistrationNumber: "",
        ProductName: "Personal Cyber",
      };

    case "0213": // Smart Device Insurance
    case "0205": // Smart Device Insurance (legacy)
      return {
        ...baseInfo,
        ModelYear: "",
        Make: "Smart Device Insurance",
        Model: policyDetails.DeviceTypeName || policyDetails.DeviceType || policyDetails.DeviceName || "Device Protection",
        RegistrationNumber: "",
        ProductName: "Smart Device",
      };

    case "0202": // Personal Accident Insurance
    case "0208": // Personal Accident Insurance
      return {
        ...baseInfo,
        CPR: policyDetails.CPR || policyDetails.InsuredCPR || "",
        ModelYear: "",
        Make: "Personal Accident Insurance",
        Model: policyDetails.CPR || policyDetails.InsuredCPR || "Personal Accident Policy",
        RegistrationNumber: "",
        ProductName: "Personal Accident",
      };

    case "0222": // Domestic Helper Insurance
      return {
        ...baseInfo,
        ModelYear: "",
        Make: "Domestic Helper Insurance",
        Model: policyDetails.Name || "Domestic Worker Coverage",
        RegistrationNumber: "",
        ProductName: "Domestic Helper",
      };

    default:
      // Fallback: Check by name for personal cyber or smart device (in case different product code)
      const productName = policyDetails.ProductName?.toLowerCase() || "";
      const planName = policyDetails.PlanName?.toLowerCase() || "";
      if (
        productName.includes("personal cyber") ||
        productName.includes("cyber") ||
        planName.includes("personal cyber") ||
        planName.includes("cyber")
      ) {
        // Fallback: Show CPR only (full name commented out for now)
        // Prioritize profileData (logged in user) like NonMotorPolicyCard does
        const fallbackCpr = profileData?.CPR || policyDetails.CPR || policyDetails.InsuredCPR || "";
        // const fallbackFirstName = profileData?.FirstName || policyDetails.Party?.FirstName || policyDetails.FirstName || "";
        // const fallbackLastName = profileData?.LastName || policyDetails.Party?.LastName || policyDetails.LastName || "";
        // const fallbackFullInfo = `${fallbackCpr} ${fallbackFirstName} ${fallbackLastName}`.trim();
        const fallbackFullInfo = fallbackCpr; // Only CPR for now

        return {
          ...baseInfo,
          CPR: fallbackCpr,
          ModelYear: "",
          Make: "Personal Cyber Insurance",
          Model: fallbackFullInfo || "Personal Cyber Policy",
          RegistrationNumber: "",
          ProductName: "Personal Cyber",
        };
      }
      if (
        productName.includes("smart device") ||
        productName.includes("device") ||
        planName.includes("smart device") ||
        planName.includes("device")
      ) {
        return {
          ...baseInfo,
          ModelYear: "",
          Make: "Smart Device Insurance",
          Model: policyDetails.DeviceTypeName || policyDetails.DeviceType || policyDetails.DeviceName || "Device Protection",
          RegistrationNumber: "",
          ProductName: "Smart Device",
        };
      }
      return baseInfo;
  }
};
